import { createContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import defaultTheme from '../themes/templates/default.json';
import { authService } from '../services/authService';
import { themeTypeToId, themeIdToType, normalizeThemeId } from '../constants/ThemeType';
import usersService from '../services/usersService';

// Ativa logs verbosos de tema apenas em DEV e com flag explícita.
// Para habilitar: VITE_THEME_DEBUG=true no .env.local + npm run dev
const THEME_DEBUG = import.meta.env.DEV && import.meta.env.VITE_THEME_DEBUG === 'true';

// Context para o tema
export const ThemeCustomContext = createContext();

/**
 * Carrega um tema pelo ID
 * @param {string} themeId - ID do tema a ser carregado
 * @returns {Promise<Object>} Configuração do tema
 */
async function loadTheme(themeId) {
  try {
    // Carrega dinamicamente o arquivo JSON do tema
    const theme = await import(`../themes/templates/${themeId}.json`);
    return theme.default || theme;
  } catch (error) {
    console.warn(`Tema "${themeId}" não encontrado. Usando tema padrão.`, error);
    return defaultTheme;
  }
}

/**
 * Mescla o tema customizado com o tema padrão
 * Garante que todas as propriedades existam, usando o padrão como fallback
 * @param {Object} customTheme - Tema customizado
 * @returns {Object} Tema mesclado
 */
// Chaves dependentes da IDENTIDADE do tema NÃO herdam do default: o soft/heading do Padrão
// (índigo/Plus Jakarta) vazaria em temas coloridos. Sem valor explícito do tema, o ThemeApplier
// deriva (primarySoft = primary+alfa; onPrimary = branco; fontFamilyHeadings = fontFamily do tema).
const NON_INHERITED_COLORS = ['primarySoft', 'onPrimary'];
const NON_INHERITED_FONTS = ['fontFamilyHeadings'];
const omit = (obj, keys) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => !keys.includes(k)));

function mergeWithDefault(customTheme) {
  return {
    id: customTheme.id || defaultTheme.id,
    name: customTheme.name || defaultTheme.name,
    description: customTheme.description || defaultTheme.description,
    colors: { ...omit(defaultTheme.colors, NON_INHERITED_COLORS), ...(customTheme.colors || {}) },
    fonts: { ...omit(defaultTheme.fonts, NON_INHERITED_FONTS), ...(customTheme.fonts || {}) },
    icons: { ...defaultTheme.icons, ...(customTheme.icons || {}) },
    labels: { ...defaultTheme.labels, ...(customTheme.labels || {}) },
    messages: { ...defaultTheme.messages, ...(customTheme.messages || {}) },
    serviceCategories: customTheme.serviceCategories || defaultTheme.serviceCategories,
    // Deep-merge: tema com customStyles PARCIAL herda as chaves que faltam do default —
    // garante que TODAS as CSS vars canônicas (--radius-*, --shadow-*) sejam emitidas
    // pelo ThemeApplier em qualquer tenant (sem vazamento de var do tema anterior).
    customStyles: { ...defaultTheme.customStyles, ...(customTheme.customStyles || {}) },
  };
}

/**
 * Provider do contexto de temas customizados
 * Gerencia o tema ativo e fornece funções para mudança
 */
export function ThemeCustomProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(defaultTheme);
  const [themeId, setThemeId] = useState('default');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Lista de temas disponíveis (pode ser expandida dinamicamente)
  const availableThemes = useMemo(() => [
    { id: 'default', name: 'Padrão' },
    { id: 'barbershop', name: 'Barbearia' },
    { id: 'company', name: 'Empresa / Negócio' },
    { id: 'tattoo-studio', name: 'Studio de Tatuagem' },
    { id: 'beauty-salon', name: 'Salão Feminino' },
    { id: 'psychology', name: 'Psicologia' },
    { id: 'aesthetics', name: 'Estética' },
    { id: 'massage', name: 'Massoterapia' },
    { id: 'dental', name: 'Odontologia' },
    { id: 'medical', name: 'Clínica Médica Geral' },
    { id: 'therapy', name: 'Terapias Holísticas' },
    { id: 'teacher', name: 'Professor' },
    { id: 'personal-trainer', name: 'Personal Trainer' },
  ], []);

  /**
   * Retorna a chave do localStorage namespaced pelo userId
   */
  const getStorageKey = () => {
    const userInfo = authService.getUserInfo();
    return userInfo?.userId ? `agendario_theme_${userInfo.userId}` : 'agendario_theme';
  };

  /**
   * Aplica um tema na tela SEM persistir (usado no boot, onde a persistência
   * é decidida por quem chama). Normaliza, carrega, mescla com o default e
   * atualiza estado + CSS variables.
   */
  const loadAndApply = async (rawThemeId) => {
    // Normaliza ids legados (ex.: 'COMPANY' salvo no localStorage por builds antigos —
    // o import dinâmico do JSON é case-sensitive e caía silenciosamente no default).
    const newThemeId = normalizeThemeId(rawThemeId);
    const loadedTheme = await loadTheme(newThemeId);
    const mergedTheme = mergeWithDefault(loadedTheme);
    setCurrentTheme(mergedTheme);
    setThemeId(newThemeId);
    applyCssVariables(mergedTheme);
    return newThemeId;
  };

  /**
   * Ação do usuário: troca de tema. Aplica na tela, grava no localStorage
   * (cache) e persiste no banco (fonte da verdade, D1).
   */
  const changeTheme = async (rawThemeId) => {
    setLoading(true);
    try {
      const newThemeId = await loadAndApply(rawThemeId);

      // Salva preferência no localStorage namespaced pelo userId
      localStorage.setItem(getStorageKey(), newThemeId);

      // Persiste no banco. Best-effort: a troca visual já aconteceu; falha
      // aqui não derruba a UI.
      try {
        await usersService.updateTheme(themeIdToType(newThemeId));
      } catch (error) {
        console.error('[THEME] Erro ao persistir tema no backend:', error);
      }
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Aplica as cores do tema como CSS variables no :root
   */
  const applyCssVariables = (theme) => {
    const root = document.documentElement;
    
    // Aplica cores
    Object.entries(theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--theme-color-${key}`, value);
    });

    // Aplica fontes
    Object.entries(theme.fonts).forEach(([key, value]) => {
      root.style.setProperty(`--theme-font-${key}`, value);
    });
  };

  /**
   * Obtém um valor do tema com fallback para o padrão
   */
  const getThemeValue = (path) => {
    const keys = path.split('.');
    let value = currentTheme;
    let defaultValue = defaultTheme;

    for (const key of keys) {
      value = value?.[key];
      defaultValue = defaultValue?.[key];
      
      if (value === undefined) {
        return defaultValue;
      }
    }

    return value !== undefined ? value : defaultValue;
  };

  /**
   * Obtém um ícone do tema
   */
  const getIcon = (iconKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.icons?.[iconKey] || defaultTheme.icons?.[iconKey] || { name: 'HelpOutline' };
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getIcon] "${iconKey}" →`, {
        found: !!theme.icons?.[iconKey],
        result,
        currentThemeId: themeId,
        hasCurrentTheme: !!currentTheme,
      });
    }
    return result;
  };

  /**
   * Obtém um label do tema
   */
  const getLabel = (labelKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.labels?.[labelKey] || defaultTheme.labels?.[labelKey] || labelKey;
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getLabel] "${labelKey}" →`, {
        found: !!theme.labels?.[labelKey],
        result,
        currentThemeId: themeId,
        hasCurrentTheme: !!currentTheme,
      });
    }
    return result;
  };

  /**
   * Obtém uma mensagem do tema
   */
  const getMessage = (messageKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.messages?.[messageKey] || defaultTheme.messages?.[messageKey] || messageKey;
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getMessage] "${messageKey}" →`, { result });
    }
    return result;
  };

  /**
   * Obtém uma cor do tema
   */
  const getColor = (colorKey) => {
    const theme = currentTheme || defaultTheme;
    const result = theme.colors?.[colorKey] || defaultTheme.colors?.[colorKey] || '#000000';
    if (THEME_DEBUG) {
      console.debug(`[ThemeCustomContext.getColor] "${colorKey}" →`, { result });
    }
    return result;
  };

  // Carrega o tema salvo no localStorage ao montar
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        // 0. IMPORTANTE: Processar token do query string ANTES de tudo
        authService.processLoginCallback();

        // 1. JWT (= banco) é a autoridade (D1): vence qualquer valor divergente no localStorage.
        const themeFromJwt = authService.getThemeFromToken();

        if (themeFromJwt) {
          console.log('[THEME] Tema encontrado no JWT (autoridade):', themeFromJwt);
          const jwtThemeId = themeTypeToId(themeFromJwt);
          await loadAndApply(jwtThemeId);
          // Reconcilia o cache local: sobrescreve qualquer valor divergente.
          localStorage.setItem(getStorageKey(), jwtThemeId);
          setInitializing(false);
          return;
        }

        // 2. Sem tema no banco (D5 → Default). localStorage só como conveniência.
        const savedThemeId = localStorage.getItem(getStorageKey());

        if (savedThemeId && savedThemeId !== 'default') {
          console.log('[THEME] Sem tema no JWT. Usando localStorage (agendario_theme):', savedThemeId);
          await loadAndApply(savedThemeId);
          setInitializing(false);
          return;
        }

        // 3. Nenhum tema encontrado - usar padrão
        console.log('[THEME] Nenhum tema encontrado. Usando tema padrão.');
        applyCssVariables(defaultTheme);
        setInitializing(false);
      } catch (error) {
        console.error('[THEME] Erro ao inicializar tema:', error);
        applyCssVariables(defaultTheme);
        setInitializing(false);
      }
    };

    initializeTheme();
  }, []);

  const value = {
    currentTheme,
    themeId,
    loading,
    initializing,
    availableThemes,
    changeTheme,
    getThemeValue,
    getIcon,
    getLabel,
    getMessage,
    getColor,
  };

  // Exibe loading durante inicialização do tema
  if (initializing) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
            Carregando tema...
          </p>
        </div>
      </div>
    );
  }

  // Sistema carregado com tema definido
  return (
    <ThemeCustomContext.Provider value={value}>
      {children}
    </ThemeCustomContext.Provider>
  );
}

ThemeCustomProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

